import { TOKEN_PROGRAM_ID, getAssociatedTokenAddress } from '@solana/spl-token'
import { BN, web3 } from 'fbonds-core'
import { createAssociatedTokenAccountInstructionIdimpotent } from 'fbonds-core/lib/common'
import { BASE_POINTS } from 'fbonds-core/lib/fbond-protocol/constants'

import { JITOSOL_MINT, JITO_STAKE_POOL, KYSOL_MINT } from '.'

type GetMintKySolInstructions = (params: {
  solAmount: BN
  kySolAmount: BN
  slippageBps?: number
  walletPublicKey: web3.PublicKey
}) => Promise<{ instructions: web3.TransactionInstruction[]; signers: web3.Signer[] }>

const STAKE_POOL_WITHDRAW_AUTHORITY = new web3.PublicKey(
  '6iQKfEyhr3bZMotVkW6beNZz5CPAkiwvgV2CTje9pVSS',
)
const RESERVE_STAKE_ACCOUNT = new web3.PublicKey('BgKUXdS29YcHCFrPm5M8oLHiTzZaMDjsebggjoaQ6KFL')
const JITO_STAKE_FEE_ACCOUNT = new web3.PublicKey('feeeFLLsam6xZJFc6UQFrHqkvVt4jfmVvi2BRLkUZ4i')
const STAKE_POOL_PROGRAM = new web3.PublicKey('SPoo1Ku8WFXoNDMHPsrGSTSG1Y47rzgn41SLUNakuHy')
const KYROS_VAULT_CONFIG = new web3.PublicKey('UwuSgAq4zByffCGCrWH87DsjfsewYjuqHfJEpzw1Jq3')
const KYROS_VAULT = new web3.PublicKey('CQpvXgoaaawDCLh8FwMZEwQqnPakRUZ5BnzhjnEBPJv')
const VAULT_PROGRAM = new web3.PublicKey('Vau1t6sLNxnzB7ZDsef8TLbPLfyZMYXH8WTNqUdm9g8')
const KYROS_FEE_WALLET = new web3.PublicKey('42iznAJXXefUPmnYz6N6GCzFvXG42o3oTd2D1ymH4UmX')

const MIN_SLIPPAGE_BPS = 100

export const getMintKySolInstructions: GetMintKySolInstructions = async ({
  solAmount,
  kySolAmount,
  slippageBps = MIN_SLIPPAGE_BPS,
  walletPublicKey,
}) => {
  if (slippageBps < MIN_SLIPPAGE_BPS) throw new Error('Mint kySOL slippage must be at least 1%')

  const mintIxns: Array<web3.TransactionInstruction> = []

  //? 1 Wrap SOL to JitoSOL =====================================================
  const temporaryAccount = web3.Keypair.generate()

  //? 1.1 Transfer SOL to temporary account
  const transferToTempAccIx = web3.SystemProgram.transfer({
    fromPubkey: walletPublicKey,
    toPubkey: temporaryAccount.publicKey,
    lamports: solAmount.toNumber(),
  })
  mintIxns.push(transferToTempAccIx)

  //? 1.2 Create ATA for wallet's jitoSOL
  const walletJitoSolAta = await getAssociatedTokenAddress(JITOSOL_MINT, walletPublicKey)
  const createIdempotentWalletJitoSolAtaIxns = createAssociatedTokenAccountInstructionIdimpotent(
    walletJitoSolAta,
    walletPublicKey,
    walletPublicKey,
    JITOSOL_MINT,
  )
  mintIxns.push(...createIdempotentWalletJitoSolAtaIxns)

  //? 1.3 Jito StakePool DepositSOL
  const depositSolIx = new web3.TransactionInstruction({
    keys: [
      { pubkey: JITO_STAKE_POOL, isSigner: false, isWritable: true }, // stake pool
      { pubkey: STAKE_POOL_WITHDRAW_AUTHORITY, isSigner: false, isWritable: false }, // stake pool withdraw authority
      { pubkey: RESERVE_STAKE_ACCOUNT, isSigner: false, isWritable: true }, // reserve stake account
      { pubkey: temporaryAccount.publicKey, isSigner: true, isWritable: true }, // depositor
      { pubkey: walletJitoSolAta, isSigner: false, isWritable: true }, // user account
      { pubkey: JITO_STAKE_FEE_ACCOUNT, isSigner: false, isWritable: true }, // fee account
      { pubkey: walletJitoSolAta, isSigner: false, isWritable: true }, // referral fee account
      { pubkey: JITOSOL_MINT, isSigner: false, isWritable: true }, // pool token mint
      { pubkey: web3.SystemProgram.programId, isSigner: false, isWritable: false }, // system program
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }, // token program
    ],
    programId: STAKE_POOL_PROGRAM,
    data: depositSolPayload(solAmount.toNumber()),
  })
  mintIxns.push(depositSolIx)

  //? 2. Create ATA for kySOL / wallet
  const walletKySolAta = await getAssociatedTokenAddress(KYSOL_MINT, walletPublicKey, true)
  const createIdempotentStakerVrtAtaIxns = createAssociatedTokenAccountInstructionIdimpotent(
    walletKySolAta,
    walletPublicKey,
    walletPublicKey,
    KYSOL_MINT,
  )
  mintIxns.push(...createIdempotentStakerVrtAtaIxns)

  //? 3 Create ATA for jitoSOL / vault
  const vaultJitoSolAta = await getAssociatedTokenAddress(JITOSOL_MINT, KYROS_VAULT, true)
  const createIdempotentVaultSupportedTokenAtaIxns =
    createAssociatedTokenAccountInstructionIdimpotent(
      vaultJitoSolAta,
      walletPublicKey,
      KYROS_VAULT,
      JITOSOL_MINT,
    )
  mintIxns.push(...createIdempotentVaultSupportedTokenAtaIxns)

  //? 4. Create ATA for kySOL / fee wallet
  const kyrosFeeWalletKySolAta = await getAssociatedTokenAddress(KYSOL_MINT, KYROS_FEE_WALLET, true)
  const createIdempotentFeeWalletVrtAtaIxns = createAssociatedTokenAccountInstructionIdimpotent(
    kyrosFeeWalletKySolAta,
    walletPublicKey,
    KYROS_FEE_WALLET,
    KYSOL_MINT,
  )
  mintIxns.push(...createIdempotentFeeWalletVrtAtaIxns)

  //? 5. Update vault balance
  const updateVaultBalanceInstruction = new web3.TransactionInstruction({
    keys: [
      { pubkey: KYROS_VAULT_CONFIG, isSigner: false, isWritable: false }, // Config account
      { pubkey: KYROS_VAULT, isSigner: false, isWritable: true }, // Vault account
      { pubkey: vaultJitoSolAta, isSigner: false, isWritable: true }, // Vault's token account
      { pubkey: KYSOL_MINT, isSigner: false, isWritable: true }, // Token mint account
      { pubkey: kyrosFeeWalletKySolAta, isSigner: false, isWritable: true }, // Vault fee token account
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }, // System program (or Token Program if required)
    ],
    programId: VAULT_PROGRAM,
    data: updateVaultBalancePayload(),
  })
  mintIxns.push(updateVaultBalanceInstruction)

  const kySolAmountLess = (kySolAmount.toNumber() * (BASE_POINTS - slippageBps)) / BASE_POINTS

  //? 6. Mint kySOL
  const mintToIx = new web3.TransactionInstruction({
    keys: [
      { pubkey: KYROS_VAULT_CONFIG, isSigner: false, isWritable: false }, // Config account
      { pubkey: KYROS_VAULT, isSigner: false, isWritable: true }, // Vault account
      { pubkey: KYSOL_MINT, isSigner: false, isWritable: true }, // Token mint account
      { pubkey: walletPublicKey, isSigner: true, isWritable: true }, // Staker account
      { pubkey: walletJitoSolAta, isSigner: false, isWritable: true }, // Staker's token account
      { pubkey: vaultJitoSolAta, isSigner: false, isWritable: true }, // Vault's token account
      { pubkey: walletKySolAta, isSigner: false, isWritable: true }, // Staker's vrt token account
      { pubkey: kyrosFeeWalletKySolAta, isSigner: false, isWritable: true }, // Vault fee token account
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }, // System program (or Token Program if required)
      { pubkey: VAULT_PROGRAM, isSigner: false, isWritable: true }, // Vault program
    ],
    programId: VAULT_PROGRAM,
    data: createMintToPayload(Math.round(kySolAmount.toNumber()), Math.round(kySolAmountLess)),
  })
  mintIxns.push(mintToIx)

  return {
    instructions: mintIxns,
    signers: [temporaryAccount],
  }
}

const createMintToPayload = (amountIn: number, minAmountOut: number): Buffer => {
  const discriminatorPlaceholder = new Uint8Array([11])

  const num1Bytes = new Uint8Array(new BigUint64Array([BigInt(amountIn)]).buffer)
  const num2Bytes = new Uint8Array(new BigUint64Array([BigInt(minAmountOut)]).buffer)

  const payloadBytes = new Uint8Array([...discriminatorPlaceholder, ...num1Bytes, ...num2Bytes])

  return Buffer.from(payloadBytes)
}

const updateVaultBalancePayload = (): Buffer => {
  const discriminatorPlaceholder = new Uint8Array([25])

  const payloadBytes = new Uint8Array([...discriminatorPlaceholder])

  return Buffer.from(payloadBytes)
}

// This one is for the stake pool program, not for the Jito (Re)staking one
const depositSolPayload = (depositAmount: number): Buffer => {
  const discriminatorPlaceholder = new Uint8Array([14])
  const num1Bytes = new Uint8Array(new BigUint64Array([BigInt(depositAmount)]).buffer)
  const payloadBytes = new Uint8Array([...discriminatorPlaceholder, ...num1Bytes])
  return Buffer.from(payloadBytes)
}
